import { useEffect, useState, useCallback, createContext, useContext, type ReactNode } from "react";
import { WifiOff, RefreshCw, Wifi } from "lucide-react";

interface NetworkStatusContextType {
  isOnline: boolean;
  isServerReachable: boolean;
}

const NetworkStatusContext = createContext<NetworkStatusContextType>({
  isOnline: true,
  isServerReachable: true,
});

export function useNetworkStatus() {
  return useContext(NetworkStatusContext);
}

export default function NetworkStatusProvider({ children }: { children: ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isServerReachable, setIsServerReachable] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [justReconnected, setJustReconnected] = useState(false);

  // 監聽瀏覽器的 online/offline 事件
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // 恢復連線後檢查伺服器是否可達
      checkServerReachability();
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // 檢查伺服器是否可達
  const checkServerReachability = useCallback(async () => {
    try {
      const response = await fetch("/api/trpc/auth.me", {
        method: "HEAD",
        credentials: "include",
        signal: AbortSignal.timeout(5000),
      });
      // 只要伺服器有回應（即使是 401），就表示可達
      if (response.ok || response.status === 401 || response.status === 403) {
        setIsServerReachable(true);
        if (showBanner) {
          setJustReconnected(true);
          setTimeout(() => {
            setShowBanner(false);
            setJustReconnected(false);
          }, 2000);
        }
        return true;
      }
      setIsServerReachable(false);
      setShowBanner(true);
      return false;
    } catch {
      setIsServerReachable(false);
      setShowBanner(true);
      return false;
    }
  }, [showBanner]);

  // 定期檢查伺服器可達性（當離線時）
  useEffect(() => {
    if (isOnline && isServerReachable) return;

    const interval = setInterval(() => {
      checkServerReachability();
    }, 10000); // 每 10 秒檢查一次

    return () => clearInterval(interval);
  }, [isOnline, isServerReachable, checkServerReachability]);

  // 監聯 fetch 錯誤來偵測伺服器不可達
  useEffect(() => {
    const originalFetch = window.fetch;
    let consecutiveFailures = 0;

    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        // 成功的請求重置失敗計數
        if (response.ok || response.status < 500) {
          consecutiveFailures = 0;
        }
        return response;
      } catch (error) {
        const url = typeof args[0] === "string" ? args[0] : (args[0] as Request)?.url || "";
        // 只追蹤 API 請求的失敗
        if (url.includes("/api/")) {
          consecutiveFailures++;
          if (consecutiveFailures >= 2) {
            setIsServerReachable(false);
            setShowBanner(true);
          }
        }
        throw error;
      }
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const handleRetry = async () => {
    setIsReconnecting(true);
    const reachable = await checkServerReachability();
    setIsReconnecting(false);
    if (reachable) {
      // 重新載入頁面以恢復所有查詢
      window.location.reload();
    }
  };

  const contextValue = { isOnline, isServerReachable };

  return (
    <NetworkStatusContext.Provider value={contextValue}>
      {showBanner && (
        <div
          className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
            justReconnected
              ? "bg-emerald-600 text-white"
              : "bg-amber-500 text-white"
          }`}
        >
          <div className="container flex items-center justify-between py-2 px-4">
            <div className="flex items-center gap-2">
              {justReconnected ? (
                <>
                  <Wifi className="h-4 w-4" />
                  <span className="text-sm font-medium">網路連線已恢復</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {!isOnline
                      ? "網路連線已中斷, 請檢查您的網路設定"
                      : "伺服器暫時無法連線, 部分功能可能無法使用"}
                  </span>
                </>
              )}
            </div>
            {!justReconnected && (
              <button
                onClick={handleRetry}
                disabled={isReconnecting}
                className="flex items-center gap-1 px-3 py-1 text-sm font-medium bg-white/20 hover:bg-white/30 rounded transition-colors disabled:opacity-50"
              >
                <RefreshCw
                  className={`h-3 w-3 ${isReconnecting ? "animate-spin" : ""}`}
                />
                {isReconnecting ? "重新連線中..." : "重試"}
              </button>
            )}
          </div>
        </div>
      )}
      {children}
    </NetworkStatusContext.Provider>
  );
}
